import React, { useCallback, useEffect, useState, useMemo } from 'react';

import Card from './components/AgendaContent/Card';
import EmptyAgenda from './components/AgendaContent/EmptyAgenda';
import Filterbar from './components/AgendaFilters/Filterbar';
import Loader from './components/AgendaContent/Loader';
import {
	today,
	getWeekNumber,
	dbDateToExactTimestamp,
	sortByTime
} from '../agenda/utils/helpers';
import MonthContainer from './components/AgendaContent/MonthContainer';

/**
	* @description		Core functionality for filtering the agenda
*/
const Filter = ({
	config,
	eventFeed,
	availableLocations,
	availableDates,
	availableDatesMonths,
	availableProductions,
	availableCategories,
	rootElement,
}) => {

	const [filteredElements, setFilteredElements] = useState([]); //result of the filtered data
	const [filteredCount, setFilteredCount] = useState(null);
	const [cards, setCards] = useState([]); //state that holds the rendered components
	const [rendering, setRendering] = useState(true);
	const [showModal, setShowModal] = useState(false);

	//state reflecting params that tweak the filtering
	const [activeFilterDate, setActiveFilterDate] = useState(false); //boolean (false = 'alle dagen'; true = checkout activeTimeStamp)
	const [activeTimestamp, setActiveTimeStamp] = useState(today); //becomes a timestamp when date filtering is used (Only one, not an array)
	const dateRange = 'month' //'month' or 'week'
	const [activeLocations, setActiveLocations] = useState([]); //empty array or one or more cinema as filter
	const [activeProductions, setActiveProductions] = useState([]);
	const [activeCategories, setActiveCategories] = useState([]);
	const [lastChanged, setLastChanged] = useState(null); //keeping track of last changed for recursive filtering
	const [isNDR, setIsNDR] = useState(false); //"No Dutch Required" filter

	// recursive filtering: available filtering options are disabled based on active other options
	const [availableLocationsFiltered, setAvailableLocationsFiltered] = useState(availableLocations);
	const [availableProductionsFiltered, setAvailableProductionsFiltered] = useState(availableProductions);
	const [availableCategoriesFiltered, setAvailableCategoriesFiltered] = useState(availableCategories);

	// memo's to avoid unnecessary rerender filterbar
	const memoizedFilteredCount = useMemo(() => filteredCount, [filteredCount]);
	const memoizedActiveFilterDate = useMemo(() => activeFilterDate, [activeFilterDate]);
	const memoizedActiveTimestamp = useMemo(() => activeTimestamp, [activeTimestamp]);
	const memoizedActiveLocations = useMemo(() => activeLocations, [activeLocations]);
	const memoizedActiveProductions = useMemo(() => activeProductions, [activeProductions]);
	const memoizedActiveCategories = useMemo(() => activeCategories, [activeCategories]);
	const memoizedIsNDR = useMemo(() => isNDR, [isNDR]);
	const memoizedAvailableLocationsFiltered = useMemo(() => availableLocationsFiltered, [availableLocationsFiltered]);
	const memoizedAvailableProductionsFiltered = useMemo(() => availableProductionsFiltered, [availableProductionsFiltered]);
	const memoizedAvailableCategoriesFiltered = useMemo(() => availableCategoriesFiltered, [availableCategoriesFiltered]);

	const updateUrl = useCallback(() => {
		const urlParams = new URLSearchParams();
		const activeDay = activeFilterDate ? activeTimestamp : 'all';
		if (activeDay) urlParams.set('f-day', encodeURI(activeDay.toString()));
		if (activeLocations.length > 0) urlParams.set('f-location', encodeURI(activeLocations.map(location => Object.keys(location)).join('+')));
		if (activeProductions.length > 0) urlParams.set('f-production', encodeURI(activeProductions.map(production => Object.keys(production)).join('+')));
		if (activeCategories.length > 0) urlParams.set('f-category', encodeURI(activeCategories.map(category => Object.keys(category)).join('+')));
		if (isNDR) urlParams.set('f-ndr', encodeURI(isNDR.toString()));
		window.history.replaceState(null, '', '?' + urlParams.toString());
	}, [activeFilterDate, activeTimestamp, activeLocations, activeProductions, activeCategories, isNDR]);

	const setDateParam = (value) => {
		if (value === 'all') {
			setActiveFilterDate(false);
			setActiveTimeStamp(null);
		} else {
			setActiveFilterDate(true);
			setActiveTimeStamp(value);
		}
		filterAvailableParameters();
		setLastChanged('date');
  	};

	const setLocationsParam = (value) => {
		if (activeLocations.includes(value)) {
			setActiveLocations([...activeLocations].filter((locationOption) => Object.keys(locationOption)[0] !== Object.keys(value)[0]));
		} else {
			setActiveLocations([...activeLocations].concat(value));
		}
		setLastChanged('location');
	};

  	const setProductionsParam = (value) => {
		if (activeProductions.includes(value)) {
			setActiveProductions([...activeProductions].filter((production) => Object.keys(production)[0] !== Object.keys(value)[0]));
		} else {
			setActiveProductions([...activeProductions].concat(value));
		}
		setLastChanged('production');
	};

	const setCategoriesParam = (value) => {
		if (activeCategories.includes(value)) {
			setActiveCategories([...activeCategories].filter((category) => Object.keys(category)[0] !== Object.keys(value)[0]));
		} else {
			setActiveCategories([...activeCategories].concat(value));
		}
		setLastChanged('category');
	};

	const setIsNDRParam = (value) => {
		setIsNDR(value);
	}

   const resetParams = () => {
		setActiveFilterDate(false);
		setActiveTimeStamp(today);
		setActiveLocations([]);
		setActiveProductions([]);
		setActiveCategories([]);
		setIsNDR(false);
		setLastChanged('reset');
  	};

	/**
	 * @description	On setting param, update the available filtering options
	 */
	const filterAvailableParameters = useCallback(() => {
		if (lastChanged === 'reset') {
			setAvailableLocationsFiltered(availableLocations);
			setAvailableProductionsFiltered(availableProductions);
			setAvailableCategoriesFiltered(availableCategories);
			return;
		}
		if (lastChanged !== 'location') {
			setAvailableLocationsFiltered(availableLocations.filter((location) => filteredElements.some((element) => element.times.some((show) => Object.keys(show.location)[0] === Object.keys(location)[0]))));
		}
		if (lastChanged !== 'production') {
			setAvailableProductionsFiltered(availableProductions.filter((production) => filteredElements.some((element) => Object.keys(element)[0] === production)));
		}
		if (lastChanged !== 'category') {
			setAvailableCategoriesFiltered(availableCategories.filter((category) => filteredElements.some((element) => element.times.some((show) => Object.keys(show.type)[0] === Object.keys(category)[0]))));
		}
	}, [availableLocations, availableProductions, availableCategories, filteredElements, lastChanged, isNDR]);

	/**
	 * @description	Helper function to split the filteredElements by show
	 * @param {Array} input - array of filtered production elements
	 * @returns {Array} - split array
	 */
	const splitShows = (input) => {
		let res = [];
		input.forEach((element) => {
			element.times &&
			element.times.forEach((show) => {
				const showCopy = structuredClone(show);
				showCopy.timestamp = dbDateToExactTimestamp(showCopy.program_start);
				const elementCopy = structuredClone(element);
				elementCopy.times = [showCopy];
				res.push(elementCopy);
			});
		});
		return sortByTime(res);
	 };

	/**
	 * @description	Main filtering functionality, activated by a change in params through useEffect below
	 */
   const filterAccordingToCurrentParams = useCallback((input) => {
		let copyInput = structuredClone(input);

		//filtering elements by production slug
		let resultProductionFilter = [];
		if (activeProductions.length > 0) {
			copyInput.forEach((element) => {
				let matchingProduction = false;
				activeProductions.forEach((production) => {
					if (Object.keys(production)[0] === element.post_id.toString()) matchingProduction = true;
				});
				if (matchingProduction) resultProductionFilter.push(element);
			});
		} else {
			resultProductionFilter = structuredClone(copyInput);
		}

		// filtering elements whether category exists in the elements tags
		let resultCategoryFilter = [];
		if (activeCategories.length > 0) {
			resultProductionFilter.forEach((element) => {
				const elementCopy = structuredClone(element);
				elementCopy.times = elementCopy.times.filter((show) => activeCategories.some(category => Object.keys(category)[0] === Object.keys(show.type)[0]));
				resultCategoryFilter.push(elementCopy);
			});
		} else {
			resultCategoryFilter = structuredClone(resultProductionFilter);
		}

		// filtering elements based on date AND return filtered times array for date
		let resultFilterDate = [];
		if (activeFilterDate && activeTimestamp) {
			resultCategoryFilter.forEach((element) => {
				const elementCopy = structuredClone(element);
				if (config.date_filter_range && dateRange) {
					if (dateRange === 'month') { //constant variable in case of toneelmakerij
						elementCopy.times = elementCopy.times.filter((show) => new Date(show.date).getMonth() === new Date(activeTimestamp).getMonth());
					} else if (dateRange === 'week') {
						elementCopy.times = elementCopy.times.filter((show) => getWeekNumber(show.date) === getWeekNumber(activeTimestamp));
					} else if (dateRange === 'weekend') {
						elementCopy.times = elementCopy.times.filter((show) => {
							if (getWeekNumber(show.date) !== getWeekNumber(activeTimestamp)) return show; //future weeks
							if (new Date(show.date).getDay() === 6 || new Date(show.date).getDay() === 7) return show; //this week & weekend
						});
					}
				} else {
					if (config.results_beyond_date) { // Flint
						elementCopy.times = elementCopy.times.filter((show) => show.date >= activeTimestamp);
					} else { //Bioscopen
						elementCopy.times = elementCopy.times.filter((show) => show.date === activeTimestamp);
					}
				}
				resultFilterDate.push(elementCopy);
			});
		} else {
			resultFilterDate = structuredClone(resultCategoryFilter);
		}

		// filtering elements on location (only adjust shows array)
		let resultFilterLocations = [];
		if (activeLocations.length > 0) {
			resultFilterDate.forEach((element) => {
				const elementCopy = structuredClone(element);
				elementCopy.times = elementCopy.times.filter((show) => activeLocations.some(location => Object.keys(location)[0] === Object.keys(show.location)[0]));
				resultFilterLocations.push(elementCopy);
			});
		} else {
			resultFilterLocations = structuredClone(resultFilterDate);
		}

		//filtering based on free or discount
		let resultFilterFree = [];
		if (isNDR) {
			resultFilterFree = resultFilterLocations.filter((element) => element.ndr);
		} else {
			resultFilterFree = structuredClone(resultFilterLocations);
		}

		return resultFilterFree;
	}, [activeFilterDate, activeTimestamp, activeLocations, activeProductions, activeCategories, config, isNDR]);

	/**
	 * @description	Main filter (reacting to a change in params)
	 */
	useEffect(() => {
		if (!eventFeed) return;
		let filterDataObj = structuredClone(eventFeed);
		const resultFiltering = filterAccordingToCurrentParams(filterDataObj.filter((el) => el['times'] && el['times'].length > 0));
		const elementsWithTimes = resultFiltering.filter((el) => el['times'] && el['times'].length > 0);
		const splitElements = splitShows(elementsWithTimes); //includes sort by time as well
		setFilteredCount(splitElements.length);
		setFilteredElements(splitElements);
	}, [eventFeed, activeFilterDate, activeTimestamp, activeLocations, activeProductions, activeCategories, filterAccordingToCurrentParams]);

	/**
	 * @description	Renders the ReactElement cards based on the filteredElements
	 */
	useEffect(() => {
		setRendering(true);
		filterAvailableParameters();

		const monthContainers = [];
		let currentMonth = null;
		const monthArray = {};
		for (let i=0; i<filteredElements.length; i++) {
			const month = new Date(filteredElements[i].times[0].date).toLocaleString('nl-NL', { month: 'long' });
			if (month !== currentMonth) {
				currentMonth = month;
				monthArray[month] = [];
				monthArray[month].push(<Card key={filteredElements[i].id} element={filteredElements[i]} />);
			} else {
				monthArray[month].push(<Card key={filteredElements[i].id} element={filteredElements[i]} />);
			}
		}
		for (const [key, value] of Object.entries(monthArray)) {
			monthContainers.push(<MonthContainer key={key} month={key}>{value}</MonthContainer>);
		}
		setCards(monthContainers);
		setRendering(false);
  	}, [activeFilterDate, activeTimestamp, config, filteredElements]);

	/**
	 * @description	Writes the current parameters to the url
	 */
	useEffect(() => {
		if (!rendering) updateUrl();
	}, [activeFilterDate, activeTimestamp, activeLocations, activeProductions, activeCategories, isNDR, rendering, updateUrl]);


	/**
	 * @description	Gets the parameters from url and sets the state accordingly
	 */
	useEffect(() => {
		if (eventFeed && window.location.search) {
			const queryParams = new URLSearchParams(window.location.search);

			if (queryParams.has('f-day')) {
				const param = decodeURI(queryParams.get('f-day') ?? '');
				if (availableDates.includes(Number(param))) {
					setActiveFilterDate(true);
					setActiveTimeStamp(Number(param));
				};
				if (param === 'all') setActiveFilterDate(false);
			}

			if (queryParams.has('f-location')) {
				const slugs = decodeURI(queryParams.get('f-location') ?? '').split('+');
				const params = availableLocations.filter((location) => slugs.includes(Object.keys(location)[0]));
				if (params.length > 0) setActiveLocations(params);
			}

			if (queryParams.has('f-production')) {
				const slugs = decodeURI(queryParams.get('f-production') ?? '').split('+');
				const params = availableProductions.filter((production) => slugs.includes(Object.keys(production)[0]));
				if (params.length > 0) setActiveProductions(params);
			}

			if (queryParams.has('f-category')) {
				const slugs = decodeURI(queryParams.get('f-category') ?? '').split('+');
				const params = availableCategories.filter((category => slugs.includes(Object.keys(category)[0])));
				if (params.length > 0) setActiveCategories(params);
			}

			if (queryParams.has('f-ndr')) {
				setIsNDR(queryParams.get('f-ndr') === 'true');
			}
		}
	}, [eventFeed, availableLocations, availableDates, availableProductions, availableCategories]);


  	if (rendering && cards.length === 0 ) return (
		<div className="loader__container--agenda">
			<Loader />
		</div>
	);

	return (
		<>
			<Filterbar
				memoizedFilteredCount={memoizedFilteredCount}

				memoizedActiveFilterDate={memoizedActiveFilterDate}
				memoizedActiveTimestamp={memoizedActiveTimestamp}
				memoizedActiveLocations={memoizedActiveLocations}
				memoizedActiveProductions={memoizedActiveProductions}
				memoizedActiveCategories={memoizedActiveCategories}
				memoizedIsNDR={memoizedIsNDR}
				isNDR={isNDR}

				availableDatesMonths={availableDatesMonths}
				availableLocations={availableLocations}
				availableProductions={availableProductions}
				availableCategories={availableCategories}

				memoizedAvailableLocationsFiltered={memoizedAvailableLocationsFiltered}
				memoizedAvailableProductionsFiltered={memoizedAvailableProductionsFiltered}
				memoizedAvailableCategoriesFiltered={memoizedAvailableCategoriesFiltered}

				setDateParam={setDateParam}
				setLocationsParam={setLocationsParam}
				setProductionsParam={setProductionsParam}
				setCategoriesParam={setCategoriesParam}
				setIsNDRParam={setIsNDRParam}
				resetParams={resetParams}

				showModal={showModal}
				setShowModal={setShowModal}
				rootElement={rootElement}
			/>
			{ (cards.length < 1)? (
				<EmptyAgenda
					text={config.empty_agenda_text}
					subtext={config.empty_agenda_subtext}
				/>
			) : (
				<>
				<div className={`js_cards-container featured agenda__month-container ${!rendering ? 'animated animated__fadeIn' : 'animated animated__preHidden'}`}>
					{cards}
				</div>
				</>
			)}
		</>
	);
};

export default React.memo(Filter);
